import { useCallback, useEffect, useState } from "react";
import { CloudUpload, X } from "lucide-react";
import { api } from "../lib/api";
import { flushQueue, offlineQueueSupported, type QueuedSubmission, type QueueResult } from "../lib/offlineQueue";
import { useLang } from "../lib/i18n";

const RESULTS_KEY = "seva.offline-results";

function send(item: QueuedSubmission) {
  const form = new FormData();
  form.append("payload", JSON.stringify(item.payload));
  for (const f of item.files) form.append("evidence", new File([f.blob], f.name, { type: f.type }));
  return api.post(item.url, form);
}

/**
 * Submits complaints queued while offline as soon as the connection
 * returns, then shows each outcome — including the one-time tracking code —
 * until the citizen dismisses it.
 */
export function OfflineQueueRunner() {
  const { tx } = useLang();
  const [results, setResults] = useState<QueueResult[]>(() => {
    try {
      return JSON.parse(localStorage.getItem(RESULTS_KEY) || "[]");
    } catch {
      return [];
    }
  });
  const save = (r: QueueResult[]) => {
    setResults(r);
    try {
      localStorage.setItem(RESULTS_KEY, JSON.stringify(r));
    } catch {
      /* private mode */
    }
  };
  const run = useCallback(async () => {
    const r = await flushQueue(send);
    if (r.length) save([...results, ...r]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [results]);
  useEffect(() => {
    if (!offlineQueueSupported()) return;
    run();
    const on = () => run();
    window.addEventListener("online", on);
    window.addEventListener("seva:queued", on);
    return () => {
      window.removeEventListener("online", on);
      window.removeEventListener("seva:queued", on);
    };
  }, [run]);
  if (!results.length) return null;
  return (
    <div className="fixed inset-x-3 bottom-3 z-50 mx-auto max-w-lg space-y-2" aria-live="polite">
      {results.map((r, i) => (
        <div key={i} className={`relative rounded-2xl border p-4 pr-10 text-[13.5px] shadow-lg ${r.ok ? "border-sage-200 bg-white" : "border-red-200 bg-red-50"}`}>
          <button className="absolute right-2 top-2 rounded-full p-1.5 cursor-pointer" aria-label={tx("Dismiss", "बंद करें")} onClick={() => save(results.filter((_, j) => j !== i))}>
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2 font-semibold">
            <CloudUpload className="h-4 w-4" /> {r.ok ? tx("Your offline complaint was submitted", "बिना इंटरनेट वाली आपकी शिकायत भेज दी गई") : tx("An offline complaint could not be submitted", "बिना इंटरनेट वाली एक शिकायत नहीं भेजी जा सकी")}
          </div>
          {r.ok ? (
            <div className="mt-1 text-ink-600">
              {tx("Reference", "शिकायत नंबर")} <span className="font-mono font-semibold text-ink-900">{r.reference}</span> · {tx("tracking code", "ट्रैकिंग कोड")}{" "}
              <span className="break-all font-mono font-semibold text-ink-900">{r.trackingToken}</span>
              <div className="text-[12px] text-ink-400">{tx("Save the tracking code — it is shown only here.", "ट्रैकिंग कोड सँभाल लीजिए — यह सिर्फ़ यहीं दिखेगा।")}</div>
            </div>
          ) : (
            <div className="mt-1 text-red-800">{r.error}</div>
          )}
        </div>
      ))}
    </div>
  );
}
